<template>
  <VContainer
    class="add-processing v2-padding"
  >
    <SLoadingOverlay :loading="loading" />
    <slot name="header">
      <VRow class="mt-0 v2-gutters">
        <VCol
          v-show="open"
          class="text-center"
        >
          <div>
            <h3 class="text-uppercase pb-1">
              {{ subscription ? $t('header.subscriptionCreateAccount') : $t('account.createAccount') }}
            </h3>
            <p class="font14 mb-2">
              {{ subscription ? $t('p.subscriptionCreateAccount') : $t('auth.p.welcome') }}
            </p>
          </div>
        </VCol>
      </VRow>
    </slot>
    <VFadeTransition hide-on-leave>
      <div>
        <VRow v-show="open" class="mt-0 v2-gutters">
          <VCol>
            <ObservedForm
              ref="observer"
              :error-messages="errors"
              data-testid="form-reg"
              @submit="submit"
            >
              <TextFieldWithValidation
                v-if="!demoData"
                v-model="firstName"
                :label="$t('label.firstName')"
                :rules="{ required: true, max: 30, su_cc: { reverse: true } }"
                name="firstName"
                :readonly="!!readonlyData?.firstName"
                hide-details-without-error
                required
                data-testid="reg-first-name"
              />
              <TextFieldWithValidation
                v-if="!demoData"
                v-model="lastName"
                :label="$t('label.lastName')"
                :rules="{ required: true, max: 30, su_cc: { reverse: true } }"
                name="lastName"
                :readonly="!!readonlyData?.lastName"
                hide-details-without-error
                required
                data-testid="reg-last-name"
              />
              <TextFieldWithValidation
                v-model="email"
                :label="$t('label.email')"
                rules="required|email|max:75"
                name="email"
                required
                :readonly="!!readonlyData?.email"
                hide-details-without-error
                data-testid="reg-email"
                autocomplete="username"
              />
              <PasswordField
                v-model="password"
                name="regPassword"
                data-testid="reg-password"
                hide-details-without-error
                autocomplete="new-password"
              />
              <PasswordField
                v-model="passwordConfirmation"
                name="confirmPwd"
                :alternate-label="$t('auth.label.confirmPwd')"
                alternate-rules="required|confirmed:@regPassword"
                hide-details-without-error
                data-testid="reg-password-confirmation"
                autocomplete="new-password"
              />
              <SBtn
                :color="btnColor"
                type="submit"
                block
                action
                class="my-2"
                data-testid="reg-submit"
              >
                {{ $t('account.createAccount') }}
              </SBtn>
              <template v-if="showSignin">
                <VDivider class="my-6" />
                <div class="text-center">
                  <p class="ma-1 font14">
                    <span>{{ $t('auth.p.alreadyCustomer') }}</span>
                    <br>
                    <span v-if="!subscription">{{ $t('auth.p.welcomeBack') }}</span>
                  </p>
                  <SBtn
                    class="font-weight-bold"
                    link-medium
                    data-testid="reg-btn-sign-in"
                    @click="openRegistration(false)"
                  >
                    {{ $t('account.signin') }}
                  </SBtn>
                </div>
              </template>
            </ObservedForm>
          </VCol>
        </VRow>
      </div>
    </VFadeTransition>
    <VRow v-show="!open" class="my-0 v2-gutters">
      <VCol class="mt-n1 mb-sm-4">
        <SBtn
          class="font-weight-bold"
          link-medium
          block
          data-testid="btn-create-account"
          @click="openRegistration(!open)"
        >
          {{ $t('account.createAccount') }}
        </SBtn>
      </VCol>
    </VRow>
  </VContainer>
</template>

<script lang="ts" setup>
import TextFieldWithValidation from '~/components/base/fields/TextFieldWithValidation.vue'
import PasswordField from '~/components/base/fields/PasswordField.vue'
import ObservedForm from '~/components/base/ObservedForm.vue'
import type { DemoAuthResponse } from '~/types/auth'
import { useAccountStore } from '~/stores/account'
import {useSAuth} from "~/composables/auth";

interface Props {
  btnColor?: string
  registrationOpen?: boolean
  subscription?: boolean
  value: boolean
  showSignin?: boolean
  doRegister?: boolean
  demoData?: DemoAuthResponse
  readonlyData?: {
    firstName?: string
    lastName?: string
    email: string
  }
}

const props = withDefaults(defineProps<Props>(), {
  btnColor: 'primary',
  showSignin: true,
  doRegister: true,
  readonlyData: undefined,
  registrationOpen: false,
  subscription: false,
  demoData: undefined
})

const emits = defineEmits<{
  (e: 'update:registration-open', value: boolean): void
  (e: 'registered', url?: string): void
  (e: 'on-registration' | 'authenticated', password: string): void
}>()

const open = ref(false)
const firstName = ref(props.readonlyData?.firstName ?? '')
const lastName = ref(props.readonlyData?.lastName ?? '')
const email = ref(props.readonlyData?.email ?? '')
const password = ref('')
const passwordConfirmation = ref('')

const observer = ref()

const openRegistration = (isOpen: boolean) => {
  open.value = isOpen
  emits('update:registration-open', isOpen)
}

const resetForm = (regOpen: boolean) => {
  if (observer.value && !regOpen) {
    observer.value.resetForm()
    firstName.value = props.readonlyData?.firstName ?? ''
    lastName.value = props.readonlyData?.lastName ?? ''
    email.value = props.readonlyData?.email ?? ''
    password.value = ''
    passwordConfirmation.value = ''
  }
}

const { processAction, loading, errors } = useAction()

watch(() => props.registrationOpen, (val) => {
  open.value = val
  errors.value = []
  resetForm(val)
}, { immediate: true })
watch(() => props.value, resetForm, { immediate: true })

const accountStore = useAccountStore()

const auth = useSAuth()
const submit = () => processAction(async () => {
  if (!props.doRegister) {
    emits('on-registration', password.value)
    return
  }
  const account = {
    customer: {
      firstName: props.demoData?.firstName ?? firstName.value,
      lastName: props.demoData?.lastName ?? lastName.value,
      demoToken: props.demoData?.demoToken,
      email: email.value,
      password: password.value
    }
  }
  const registeredData = await accountStore.createAccount(account)
  emits('registered', registeredData.redirectUrl)

  if (!props.demoData) {
    await auth.login({
      username: email.value,
      password: password.value
    })
    emits('authenticated', password.value)
  }
})
</script>

<style lang="scss" scoped>
</style>
